import {useCallback, useRef} from 'react';

import {TRUST_STATUS_GROUP_ORDER} from '@/features/trust-accounts/utils';
import {useCollapsedKeys} from '@/hooks';

import {getGuestStatusGroupKey} from '../utils';

import type {TrustAccountGroupFold} from '@/features/trust-accounts/utils';
import type {TrustStatus} from '@sollapay/enums';

const STORAGE_KEY = 'guest:collapsedStatusGroups';

interface FirmFold {
  signature: string;
  fold: TrustAccountGroupFold;
}

/**
 * Which status groups the guest folded inside each firm, remembered in the browser and shared by the
 * table and the card view; every group starts expanded. A firm's fold keeps its identity until that
 * firm's own groups change, so folding in one firm doesn't re-render the other (memoised) firm cards.
 */
export function useGuestCollapsedStatusGroups(): (firmId: string) => TrustAccountGroupFold {
  const {isCollapsed, toggle} = useCollapsedKeys(STORAGE_KEY);
  const foldsByFirm = useRef(new Map<string, FirmFold>());

  return useCallback(
    (firmId: string) => {
      const collapsed = TRUST_STATUS_GROUP_ORDER.filter(status =>
        isCollapsed(getGuestStatusGroupKey(firmId, status))
      );
      const signature = collapsed.join();
      const cached = foldsByFirm.current.get(firmId);
      if (cached?.signature === signature) return cached.fold;

      const collapsedStatuses = new Set<TrustStatus>(collapsed);
      const fold: TrustAccountGroupFold = {
        isCollapsed: status => collapsedStatuses.has(status),
        toggle: status => toggle(getGuestStatusGroupKey(firmId, status))
      };
      foldsByFirm.current.set(firmId, {signature, fold});
      return fold;
    },
    [isCollapsed, toggle]
  );
}
